/**
 * Pure rules for the offline side activities: tier progression, race standings and the pot, minicab
 * fares and tips, matchbook rewards. No engine imports; unit-tested in scoring.test.ts. Money is whole
 * pounds, as everywhere in the single-player economy.
 */
import type { RewardLine } from '../missions/types';
import type { TieredRecord } from './state';

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);

// ----------------------------------------------------------------------------- tiers

/**
 * Book a finished run against the record. Winning (or, for a shift, earning enough) the highest tier
 * unlocked opens the next one, which is offered next. A loss offers the same tier again; once every tier
 * is open, wins rotate through them so all the best scores stay in play.
 */
export function advanceTier(rec: TieredRecord, tier: number, won: boolean, tiers: number): { unlockedNew: number | null } {
  rec.runs++;
  if (won) {
    rec.wins++;
    if (!rec.cleared.includes(tier)) rec.cleared = [...rec.cleared, tier].sort((a, b) => a - b);
  }
  let unlockedNew: number | null = null;
  if (won && tier >= rec.unlocked && rec.unlocked < tiers) {
    rec.unlocked = tier + 1;
    unlockedNew = rec.unlocked;
  }
  if (!won) rec.next = tier;
  else if (unlockedNew) rec.next = unlockedNew;
  else rec.next = rec.unlocked >= tiers ? (tier % tiers) + 1 : rec.unlocked;
  rec.next = Math.max(1, Math.min(rec.unlocked, rec.next));
  return { unlockedNew };
}

/** Lower is better (race times); 0 means "no score yet". Returns true when `score` is a new best. */
export function recordLow(rec: TieredRecord, tier: number, score: number): boolean {
  const i = tier - 1;
  const old = rec.best[i] ?? 0;
  if (old > 0 && score >= old) return false;
  rec.best[i] = Math.round(score * 10) / 10;
  return true;
}

/** Higher is better (cab takings). Returns true when `score` is a new best. */
export function recordHigh(rec: TieredRecord, tier: number, score: number): boolean {
  const i = tier - 1;
  if (score <= (rec.best[i] ?? 0)) return false;
  rec.best[i] = Math.round(score);
  return true;
}

/** "1:32.4" */
export function formatRaceTime(seconds: number): string {
  const s = Math.max(0, seconds);
  const m = Math.floor(s / 60);
  const r = s - m * 60;
  return `${m}:${r < 10 ? '0' : ''}${r.toFixed(1)}`;
}

export function ordinal(n: number): string {
  const t = n % 100;
  if (t >= 11 && t <= 13) return `${n}th`;
  return `${n}${['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'}`;
}

// ----------------------------------------------------------------------------- races

/** One car's progress through a race. */
export interface RacerProgress {
  id: string;
  /** Checkpoints passed (the finish is the last one). */
  passed: number;
  /** Metres to the next checkpoint (ignored once finished). */
  toNext: number;
  /** Race seconds at the finish, or null while still racing. */
  finishedAt: number | null;
  /** Out of the race (wrecked, carjacked): always last. */
  out?: boolean;
}

/** Running order: finishers by time, then by checkpoints passed and distance to the next; retired last. */
export function standings(racers: readonly RacerProgress[]): RacerProgress[] {
  return [...racers].sort((a, b) => {
    if (!!a.out !== !!b.out) return a.out ? 1 : -1;
    const fa = a.finishedAt !== null;
    const fb = b.finishedAt !== null;
    if (fa !== fb) return fa ? -1 : 1;
    if (fa && fb) return a.finishedAt! - b.finishedAt!;
    if (a.passed !== b.passed) return b.passed - a.passed;
    return a.toNext - b.toNext;
  });
}

/** 1-based place of `id` in the running order. */
export function placeOf(racers: readonly RacerProgress[], id: string): number {
  return standings(racers).findIndex((r) => r.id === id) + 1;
}

/**
 * What a winner takes home. Everyone on the grid puts the stake on the bonnet; first past the post takes
 * the pot (their own stake back included — it was paid at the start). A first-ever win on a course and a
 * course record (beating your own best) each add a little from the crowd.
 */
export function racePayout(o: { stake: number; entrants: number; tier: number; firstWin: boolean; record: boolean }): RewardLine[] {
  const lines: RewardLine[] = [{ label: `The pot (${o.entrants} × £${o.stake})`, amount: o.stake * o.entrants }];
  if (o.firstWin) lines.push({ label: 'First win on this course', amount: 50 * o.tier });
  if (o.record) lines.push({ label: 'Course record', amount: 40 * o.tier });
  return lines;
}

/**
 * Rival pace for a tier and the gap to the player: a touch of rubber-banding so a race stays a race
 * (never more than ±12 %), on top of the tier's pace. `gap` = metres the rival is ahead (+) or behind (−).
 */
export function rivalPace(base: number, gap: number): number {
  const k = gap > 0 ? -Math.min(0.12, Math.max(0, gap - 60) / 900) : Math.min(0.12, Math.max(0, -gap - 60) / 900);
  return base * (1 + k);
}

// ----------------------------------------------------------------------------- minicab fares

/** Tier multipliers on the meter, and the average speed (m/s) the "knowledge" par time assumes. */
export const CAB_RATE = [1, 1.35, 1.8, 2.1] as const;
export const CAB_PAR_SPEED = [8, 9, 10, 10.5] as const;

/** The meter for a fare of `metres` (road route) on `tier`. */
export function fareBase(metres: number, tier: number): number {
  return Math.round((12 + metres * 0.08) * (CAB_RATE[tier - 1] ?? 1));
}

/** Par seconds for the route: what a cabbie with the knowledge would take, plus time to pull away. */
export function farePar(metres: number, tier: number): number {
  return metres / (CAB_PAR_SPEED[tier - 1] ?? 8) + 12;
}

/** Speed tip: half the meter at 60 % of par or better, nothing from 140 % of par. */
export function speedTip(base: number, seconds: number, par: number): number {
  return Math.round(base * 0.5 * clamp01((1.4 - seconds / Math.max(1, par)) / 0.8));
}

/**
 * Care tip: up to 40 % of the meter. Lost body condition (fraction of max health) and hard knocks eat it;
 * 20 % condition lost or four hard knocks and there's nothing. Any police attention during the ride: nothing.
 */
export function careTip(base: number, damageFrac: number, hardHits: number, lawSeen: boolean): number {
  if (lawSeen) return 0;
  const care = clamp01(1 - damageFrac / 0.2 - hardHits * 0.25);
  return Math.round(base * 0.4 * care);
}

export interface FareResult {
  base: number;
  speed: number;
  care: number;
  /** The passenger got out (law on your tail): nothing paid. */
  bailed: boolean;
}

export function fareTotal(f: FareResult): number {
  return f.bailed ? 0 : f.base + f.speed + f.care;
}

/** A shift unlocks the next tier when the takings beat the meters by 30 % (i.e. it needed tips). */
export function shiftTarget(bases: readonly number[]): number {
  return Math.round(bases.reduce((s, b) => s + b, 0) * 1.3);
}

// ----------------------------------------------------------------------------- matchbooks

/** Lenny pays this much for every number found in a matchbook. */
export const MATCHBOOK_FIND = 20;
/** Milestones (matchbooks found) and Lenny's bonus for each. */
export const MATCHBOOK_MILESTONES: readonly { count: number; bonus: number }[] = [
  { count: 4, bonus: 100 },
  { count: 8, bonus: 250 },
  { count: 12, bonus: 500 },
  { count: 16, bonus: 1000 },
];

/** Milestones reached at `found` that haven't been paid yet. */
export function dueMilestones(found: number, paid: readonly number[]): { count: number; bonus: number }[] {
  return MATCHBOOK_MILESTONES.filter((m) => found >= m.count && !paid.includes(m.count));
}
